// components/ProductCardPanel.jsx
import React from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import "../styles/productCardPanel.css";

const ServicePopupContent = ({ serviceKey }) => {
  const content = {
    // =====================
    // TAX ARENA SERVICES
    // =====================

    EWB: (
      <>
        <div className="panel-section">
          <h2 className="panel-modal-title">
            EWB Bill and Self Billing Reporting
          </h2>
          <p className="panel-modal-text">
            Our E-Way Bill (EWB) and Self Billing Reporting solution automates
            the generation, validation, and management of e-way bills directly
            from your ERP system. We eliminate manual intervention, reduce
            compliance risks, and ensure every goods movement is tracked
            accurately in real time.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">What is E-Way Bill?</h3>
          <p className="panel-modal-text">
            An E-Way Bill is a mandatory electronic document required for the
            movement of goods exceeding a specified value. It serves as proof
            that goods are being transported in compliance with GST regulations
            and must be generated before the commencement of goods movement.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">What is Self Billing?</h3>
          <p className="panel-modal-text">
            Self Billing is a process where the buyer generates the invoice on
            behalf of the supplier. This is common in industries with recurring
            procurement from unregistered dealers or specific vendor
            arrangements. Our solution automates this process, ensuring accurate
            tax computation and compliant document generation.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Our Solution Covers:</h3>
          <ul className="panel-modal-list">
            <li>
              Automated EWB generation directly from Sales Orders, Invoices, and
              Delivery Notes within your ERP
            </li>
            <li>
              Real-time validation of transporter IDs, HSN codes, and distance
              calculations
            </li>
            <li>
              Bulk generation and cancellation of e-way bills for high-volume
              operations
            </li>
            <li>
              Self billing invoice creation with automatic tax computation for
              unregistered vendors
            </li>
            <li>
              Consolidated EWB for multiple invoices shipped in a single
              consignment
            </li>
            <li>
              Auto-extension of EWB validity based on distance and transport
              mode
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Key Benefits:</h3>
          <ul className="panel-modal-list">
            <li>
              <strong>Zero Manual Entry</strong> — EWBs are generated
              automatically from existing transaction data
            </li>
            <li>
              <strong>Error Reduction</strong> — System validates all fields
              before submission to the government portal
            </li>
            <li>
              <strong>Audit Trail</strong> — Complete tracking of generated,
              modified, and cancelled EWBs
            </li>
            <li>
              <strong>Compliance Assurance</strong> — Stay updated with latest
              regulatory changes through automatic updates
            </li>
            <li>
              <strong>Time Savings</strong> — Reduce EWB processing time by up
              to 80%
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Supported ERP Platforms:</h3>
          <p className="panel-modal-text">
            Oracle Fusion, Oracle EBS, SAP S/4HANA, Oracle NetSuite, Odoo,
            Microsoft Dynamics 365, Tally, Zoho Books, and QuickBooks.
          </p>
        </div>
      </>
    ),

    VENDOR_BLOCKING: (
      <>
        <div className="panel-section">
          <h2 className="panel-modal-title">Vendor Payment Blocking</h2>
          <p className="panel-modal-text">
            Our Vendor Payment Blocking solution integrates your ERP with GST
            portal data (GSTR-2A/2B) to automatically block payments for vendors
            whose invoices are not reflected in the GST returns. This ensures
            your organization only processes payments for compliant
            transactions, protecting your Input Tax Credit (ITC) and reducing
            financial risk.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">How It Works:</h3>

          <h3 className="panel-modal-subtitle">
            Step 1 — GSTR-2A/2B Data Sync
          </h3>
          <p className="panel-modal-text">
            Our system automatically fetches GSTR-2A and GSTR-2B data from the
            GST portal at configurable intervals. This data contains all
            invoices filed by your vendors and serves as the source of truth for
            matching.
          </p>

          <h3 className="panel-modal-subtitle">Step 2 — Invoice Matching</h3>
          <p className="panel-modal-text">
            Each vendor invoice recorded in your ERP is matched against the
            corresponding entry in GSTR-2A/2B. The system checks invoice number,
            date, taxable value, tax amount, and GSTIN to determine match
            accuracy.
          </p>

          <h3 className="panel-modal-subtitle">Step 3 — Automatic Blocking</h3>
          <p className="panel-modal-text">
            Invoices that are unmatched or have discrepancies are automatically
            flagged, and the corresponding vendor payments are blocked in the
            ERP. Payment release only happens after reconciliation is completed
            and approved.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Matching Categories:</h3>
          <ul className="panel-modal-list">
            <li>
              <strong>Exact Match</strong> — All fields match perfectly; payment
              is released automatically
            </li>
            <li>
              <strong>Partial Match</strong> — Minor discrepancies in amount or
              date; flagged for review
            </li>
            <li>
              <strong>Mismatch</strong> — Significant differences found; payment
              blocked until resolved
            </li>
            <li>
              <strong>Missing in 2A/2B</strong> — Invoice not reported by
              vendor; payment fully blocked
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Key Benefits:</h3>
          <ul className="panel-modal-list">
            <li>Protect your ITC claims by paying only verified invoices</li>
            <li>Reduce manual reconciliation effort by up to 90%</li>
            <li>
              Automated alerts and notifications to vendors for pending
              compliance
            </li>
            <li>
              Configurable tolerance levels for amount and date mismatches
            </li>
            <li>
              Complete dashboard with real-time blocking and release status
            </li>
            <li>
              Full audit trail for every block, unblock, and override action
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">
            Why Vendor Payment Blocking Matters:
          </h3>
          <p className="panel-modal-text">
            Under the GST regime, ITC can only be claimed if the supplier has
            filed the corresponding return. Paying vendors whose invoices are
            not reflected in GSTR-2A/2B exposes your organization to ITC
            reversal, interest, and penalties. Our solution proactively prevents
            this risk at the source — the payment process itself.
          </p>
        </div>
      </>
    ),

    VAT_RECON: (
      <>
        <div className="panel-section">
          <h2 className="panel-modal-title">
            VAT Reconciliation — AP, AR and GL
          </h2>
          <p className="panel-modal-text">
            Our VAT Reconciliation solution provides end-to-end reconciliation
            across Accounts Payable (AP), Accounts Receivable (AR), and General
            Ledger (GL) to ensure your VAT/GST reporting is accurate, complete,
            and audit-ready. We identify discrepancies before they become
            compliance issues.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">
            AP Reconciliation (Purchase Side)
          </h3>
          <p className="panel-modal-text">
            We reconcile your purchase invoices recorded in the ERP with
            GSTR-2A/2B data from the GST portal. Every supplier invoice is
            matched on GSTIN, invoice number, date, taxable value, and tax
            amounts. Discrepancies are categorized and flagged for action —
            ensuring your ITC claims are accurate and defensible.
          </p>
          <ul className="panel-modal-list">
            <li>Three-way matching: PO vs. GRN vs. Invoice vs. GSTR-2A/2B</li>
            <li>Automated identification of duplicate invoices</li>
            <li>Tolerance-based matching with configurable thresholds</li>
            <li>Vendor-wise reconciliation summary and exception reports</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">
            AR Reconciliation (Sales Side)
          </h3>
          <p className="panel-modal-text">
            We match your outward supply invoices from the ERP with GSTR-1
            filings to ensure every sale is reported correctly. This prevents
            revenue leakage, incorrect tax liability computation, and notices
            from the tax department.
          </p>
          <ul className="panel-modal-list">
            <li>Invoice-level matching between ERP sales data and GSTR-1</li>
            <li>
              Identification of unreported invoices and credit/debit notes
            </li>
            <li>HSN-wise and rate-wise tax liability comparison</li>
            <li>Customer-wise reconciliation with exportable reports</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">GL Reconciliation</h3>
          <p className="panel-modal-text">
            We reconcile your General Ledger tax accounts with the actual tax
            returns filed. This ensures the tax liability and ITC reflected in
            your books matches what has been reported to the government —
            critical for financial audits and assessments.
          </p>
          <ul className="panel-modal-list">
            <li>GL balance vs. GSTR-3B liability and ITC comparison</li>
            <li>Period-wise breakup of tax collected vs. tax reported</li>
            <li>Identification of timing differences and adjusting entries</li>
            <li>Year-end reconciliation support for annual return (GSTR-9)</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Key Benefits:</h3>
          <ul className="panel-modal-list">
            <li>Single dashboard for AP, AR, and GL reconciliation status</li>
            <li>Reduce manual reconciliation effort by up to 85%</li>
            <li>Audit-ready reports with drill-down capability</li>
            <li>
              Proactive identification of compliance gaps before filing
              deadlines
            </li>
            <li>Support for multi-GSTIN and multi-entity organizations</li>
          </ul>
        </div>
      </>
    ),

    DIRECT_TAX: (
      <>
        <div className="panel-section">
          <h2 className="panel-modal-title">Direct Tax — One-Way Sync</h2>
          <p className="panel-modal-text">
            Our Direct Tax One-Way Sync solution automates the extraction and
            synchronization of TDS (Tax Deducted at Source), TCS (Tax Collected
            at Source), and Advance Tax data from your ERP to the Income Tax
            portal. This ensures timely and accurate filing of direct tax
            returns with zero manual data handling.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">What is One-Way Sync?</h3>
          <p className="panel-modal-text">
            One-Way Sync means data flows from your ERP system to the tax portal
            in a single direction. Your ERP remains the single source of truth,
            and all TDS/TCS deduction entries, challan details, and deductee
            records are automatically pushed to the government portal for return
            filing — eliminating re-entry, copy-paste errors, and version
            mismatches.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Coverage:</h3>
          <ul className="panel-modal-list">
            <li>
              <strong>TDS on Payments</strong> — Automated computation of TDS on
              vendor payments based on Section, PAN availability, and threshold
              limits
            </li>
            <li>
              <strong>TCS on Collections</strong> — Auto-calculation of TCS on
              sales exceeding specified thresholds under applicable sections
            </li>
            <li>
              <strong>Challan Generation</strong> — Automated challan
              preparation with BSR code, challan serial number, and deposit date
              mapping
            </li>
            <li>
              <strong>Form 26Q / 27Q / 24Q</strong> — Auto-population of
              quarterly TDS return data from ERP transactions
            </li>
            <li>
              <strong>Form 16 / 16A</strong> — Generation of TDS certificates
              for employees and vendors
            </li>
            <li>
              <strong>Advance Tax Tracking</strong> — Quarterly advance tax
              liability computation and payment tracking
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">How It Works:</h3>
          <ul className="panel-modal-list">
            <li>
              ERP captures every deduction event with section code, rate, PAN,
              and amount
            </li>
            <li>
              Data is validated against business rules and regulatory thresholds
            </li>
            <li>
              Validated data is formatted and synced to the TRACES / Income Tax
              portal
            </li>
            <li>
              Filing confirmation and acknowledgement numbers are logged back
              for audit trail
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Key Benefits:</h3>
          <ul className="panel-modal-list">
            <li>Eliminate manual data entry for TDS/TCS return preparation</li>
            <li>
              Reduce late filing penalties with automated deadline tracking
            </li>
            <li>
              Ensure PAN validation and Section accuracy at the point of
              deduction
            </li>
            <li>
              Centralized view of all direct tax obligations across entities
            </li>
            <li>Complete audit trail from ERP transaction to filed return</li>
          </ul>
        </div>
      </>
    ),

    BANK_RECON: (
      <>
        <div className="panel-section">
          <h2 className="panel-modal-title">
            Bank Statement and Reconciliation
          </h2>
          <p className="panel-modal-text">
            Our Bank Statement and Reconciliation solution automates the import,
            parsing, and matching of bank statements with your ERP transactions.
            Whether you deal with hundreds or thousands of transactions daily,
            our solution identifies matched, unmatched, and partially matched
            entries — giving your finance team complete visibility and control.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">How It Works:</h3>

          <h3 className="panel-modal-subtitle">
            Step 1 — Bank Statement Import
          </h3>
          <p className="panel-modal-text">
            Bank statements are imported automatically via direct bank feeds
            (APIs), SFTP, or manual upload in MT940, BAI2, CSV, PDF, or Excel
            formats. Our parser normalizes the data regardless of bank format
            variations.
          </p>

          <h3 className="panel-modal-subtitle">
            Step 2 — Intelligent Matching
          </h3>
          <p className="panel-modal-text">
            The system matches bank entries against ERP records (payments,
            receipts, journal entries) using configurable rules — amount, date,
            reference number, narration keywords, and customer/vendor mapping.
            Multi-level matching supports one-to-one, one-to-many, and
            many-to-one scenarios.
          </p>

          <h3 className="panel-modal-subtitle">Step 3 — Exception Handling</h3>
          <p className="panel-modal-text">
            Unmatched and partially matched entries are routed to a
            reconciliation workbench where finance teams can review, manually
            match, create adjustment entries, or flag items for investigation.
            Every action is logged for audit purposes.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Features:</h3>
          <ul className="panel-modal-list">
            <li>Multi-bank and multi-currency statement processing</li>
            <li>
              Automated matching with configurable tolerance for amounts and
              dates
            </li>
            <li>Bank charge and interest auto-identification and booking</li>
            <li>Cheque clearance tracking with aging analysis</li>
            <li>Opening and closing balance validation per statement period</li>
            <li>BRS (Bank Reconciliation Statement) generation for audit</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Key Benefits:</h3>
          <ul className="panel-modal-list">
            <li>
              <strong>90%+ Auto-Match Rate</strong> — Drastically reduce manual
              reconciliation effort
            </li>
            <li>
              <strong>Daily Reconciliation</strong> — Move from monthly to daily
              reconciliation cycles
            </li>
            <li>
              <strong>Cash Visibility</strong> — Real-time view of cash position
              across all bank accounts
            </li>
            <li>
              <strong>Fraud Detection</strong> — Quickly identify unauthorized
              transactions or duplicate payments
            </li>
            <li>
              <strong>Audit Readiness</strong> — Complete trail from bank
              statement line to ERP journal entry
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Supported Formats:</h3>
          <p className="panel-modal-text">
            MT940, MT942, BAI2, CAMT.053, CSV, Excel, PDF (with OCR extraction).
            Compatible with all major banks including HDFC, ICICI, SBI, Axis,
            Kotak, Standard Chartered, HSBC, Citi, and more.
          </p>
        </div>
      </>
    ),

    // =====================
    // NON-TAX ARENA SERVICES
    // =====================

    ORACLE_DEV: (
      <>
        <div className="panel-section">
          <h2 className="panel-modal-title">
            Oracle BIP, OIC, VBCS & APEX Development
          </h2>
          <p className="panel-modal-text">
            We provide end-to-end development services across Oracle's key
            technology platforms — BI Publisher (BIP), Oracle Integration Cloud
            (OIC), Visual Builder Cloud Service (VBCS), and Oracle APEX. Our
            certified Oracle developers build custom solutions that extend your
            ERP capabilities, automate integrations, and deliver powerful user
            experiences.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Oracle BI Publisher (BIP)</h3>
          <p className="panel-modal-text">
            We design and develop pixel-perfect reports, documents, and layouts
            using Oracle BI Publisher. From complex financial statements to
            customer-facing invoices and regulatory reports — our BIP solutions
            deliver formatted output in PDF, Excel, HTML, CSV, and XML.
          </p>
          <ul className="panel-modal-list">
            <li>Custom report templates with dynamic formatting</li>
            <li>
              Data model design with multiple data sources (SQL, Web Services,
              REST APIs)
            </li>
            <li>Bursting and scheduled delivery via email and SFTP</li>
            <li>
              Sub-template and translation support for multi-language reports
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">
            Oracle Integration Cloud (OIC)
          </h3>
          <p className="panel-modal-text">
            We build robust, scalable integrations using Oracle OIC to connect
            your Oracle Cloud applications with third-party systems, on-premise
            databases, REST/SOAP APIs, and file-based data sources. Our
            integrations are designed for reliability, monitoring, and error
            handling.
          </p>
          <ul className="panel-modal-list">
            <li>Application-driven and scheduled integration patterns</li>
            <li>
              Pre-built adapters for Oracle Fusion, EBS, NetSuite, Salesforce,
              SAP, and more
            </li>
            <li>File-based integrations with SFTP, FTP, and Object Storage</li>
            <li>Error handling, retry logic, and monitoring dashboards</li>
            <li>Process automation using OIC Process Builder</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Oracle VBCS (Visual Builder)</h3>
          <p className="panel-modal-text">
            We create custom web and mobile applications using Oracle Visual
            Builder Cloud Service. VBCS enables rapid application development
            with drag-and-drop UI components, built-in REST API consumption, and
            seamless Oracle SaaS integration.
          </p>
          <ul className="panel-modal-list">
            <li>Custom pages extending Oracle Fusion Cloud functionality</li>
            <li>Responsive web apps with role-based access and workflows</li>
            <li>Mobile-ready applications with offline capability</li>
            <li>Business object modeling and custom REST endpoint creation</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Oracle APEX</h3>
          <p className="panel-modal-text">
            We develop feature-rich, low-code applications using Oracle APEX for
            internal tools, dashboards, approval workflows, and data management
            applications. APEX provides enterprise-grade security, scalability,
            and Oracle Database integration out of the box.
          </p>
          <ul className="panel-modal-list">
            <li>Interactive reports, dashboards, and chart visualizations</li>
            <li>Approval workflow applications with email notifications</li>
            <li>Data entry forms with validation and audit logging</li>
            <li>REST API integration and external data consumption</li>
            <li>Migration of legacy Access/Excel tools to Oracle APEX</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Why Choose Us:</h3>
          <ul className="panel-modal-list">
            <li>Oracle certified developers with 10+ years experience</li>
            <li>150+ successful integrations delivered</li>
            <li>Agile development with 2-week sprint cycles</li>
            <li>Post-deployment support and knowledge transfer</li>
          </ul>
        </div>
      </>
    ),

    DATA_MIGRATION: (
      <>
        <div className="panel-section">
          <h2 className="panel-modal-title">Data Migration Projects</h2>
          <p className="panel-modal-text">
            We execute end-to-end data migration projects that safely and
            accurately move your business-critical data from legacy systems to
            modern ERP platforms. Our proven methodology ensures data integrity,
            minimal downtime, and complete validation — so your business never
            misses a beat during the transition.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Our Migration Methodology:</h3>

          <h3 className="panel-modal-subtitle">Phase 1 — Assessment</h3>
          <p className="panel-modal-text">
            We analyze your source system data — volume, structure, quality, and
            dependencies. We identify data gaps, redundancies, and
            transformation requirements. A detailed migration plan with
            timelines, resource allocation, and risk mitigation strategies is
            prepared.
          </p>

          <h3 className="panel-modal-subtitle">
            Phase 2 — Mapping & Transformation
          </h3>
          <p className="panel-modal-text">
            We design field-level mapping between source and target systems.
            Data cleansing rules, transformation logic, default values, and
            validation criteria are defined. Complex transformations including
            currency conversion, code mapping, and data enrichment are handled
            programmatically.
          </p>

          <h3 className="panel-modal-subtitle">
            Phase 3 — Mock Migration & Testing
          </h3>
          <p className="panel-modal-text">
            We run multiple mock migration cycles with increasing data volumes.
            Each cycle includes automated validation reports comparing source
            counts, totals, and record accuracy against the target system.
            Issues are identified, logged, and resolved iteratively.
          </p>

          <h3 className="panel-modal-subtitle">Phase 4 — Cutover & Go-Live</h3>
          <p className="panel-modal-text">
            The final migration is executed during a planned cutover window with
            minimal business disruption. Post-migration validation confirms data
            completeness and accuracy. Rollback procedures are in place as a
            safety net.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Data Objects We Migrate:</h3>
          <ul className="panel-modal-list">
            <li>Chart of Accounts and GL Balances</li>
            <li>Customer and Vendor Master Records</li>
            <li>Item/Product Master with BOMs and Pricing</li>
            <li>Open AP and AR Transactions</li>
            <li>Fixed Assets and Depreciation History</li>
            <li>Inventory Balances and Lot/Serial Data</li>
            <li>Employee and Payroll Data</li>
            <li>Historical Transactions (as needed)</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Key Benefits:</h3>
          <ul className="panel-modal-list">
            <li>
              <strong>Zero Data Loss</strong> — Rigorous validation at every
              stage ensures 100% data integrity
            </li>
            <li>
              <strong>Minimal Downtime</strong> — Optimized cutover planning
              reduces business disruption
            </li>
            <li>
              <strong>Clean Data</strong> — Migration is an opportunity to
              cleanse and standardize your master data
            </li>
            <li>
              <strong>Proven Tools</strong> — We use FBDI, ADFdi, REST APIs, and
              custom loaders based on the target platform
            </li>
            <li>
              <strong>Experienced Team</strong> — 50+ successful migrations
              across Oracle, SAP, NetSuite, and Odoo
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Supported Platforms:</h3>
          <p className="panel-modal-text">
            Oracle Fusion Cloud, Oracle EBS, Oracle NetSuite, SAP S/4HANA, Odoo,
            Microsoft Dynamics 365, Sage, Tally, Zoho Books, and QuickBooks. We
            support migrations between any combination of these systems.
          </p>
        </div>
      </>
    ),

    ERP_LIFT_SHIFT: (
      <>
        <div className="panel-section">
          <h2 className="panel-modal-title">ERP Lift & Shift</h2>
          <p className="panel-modal-text">
            Our ERP Lift & Shift service moves your existing ERP environment
            from on-premise infrastructure or one cloud provider to another —
            with minimal disruption, zero data loss, and full operational
            continuity. Whether you're moving to Oracle Cloud Infrastructure
            (OCI), AWS, Azure, or Google Cloud, we handle the complete migration
            lifecycle.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">What is Lift & Shift?</h3>
          <p className="panel-modal-text">
            Lift & Shift (also called "rehosting") is the process of moving your
            ERP application and its associated databases, configurations,
            customizations, and integrations to a new hosting environment
            without re-architecting the application. The goal is to achieve the
            benefits of cloud infrastructure — scalability, reliability, cost
            optimization — with minimal changes to the application layer.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Our Approach:</h3>
          <ul className="panel-modal-list">
            <li>
              <strong>Discovery & Assessment</strong> — Complete inventory of
              servers, databases, middleware, integrations, customizations, and
              dependencies
            </li>
            <li>
              <strong>Architecture Design</strong> — Target environment sizing,
              network topology, security groups, and high availability
              configuration
            </li>
            <li>
              <strong>Environment Setup</strong> — Provisioning of target
              infrastructure with matching or upgraded specifications
            </li>
            <li>
              <strong>Migration Execution</strong> — Application binaries,
              database exports/imports, configuration replication, and
              integration endpoint updates
            </li>
            <li>
              <strong>Testing & Validation</strong> — End-to-end functional
              testing, performance benchmarking, and integration validation
            </li>
            <li>
              <strong>Cutover & Go-Live</strong> — DNS switching, final data
              sync, smoke testing, and user acceptance
            </li>
            <li>
              <strong>Hypercare</strong> — 2-4 weeks of dedicated post-migration
              support and monitoring
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">We Handle:</h3>
          <ul className="panel-modal-list">
            <li>Oracle EBS R12 to OCI migration</li>
            <li>Oracle Database migration (on-premise to cloud)</li>
            <li>WebLogic, Forms, and Reports server migration</li>
            <li>Third-party integration endpoint reconfiguration</li>
            <li>SSL certificate, domain, and network configuration</li>
            <li>Disaster recovery and backup setup on new infrastructure</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Key Benefits:</h3>
          <ul className="panel-modal-list">
            <li>
              <strong>Cost Reduction</strong> — Eliminate on-premise hardware
              costs and reduce TCO by 30-50%
            </li>
            <li>
              <strong>Scalability</strong> — Scale compute and storage on-demand
              based on business needs
            </li>
            <li>
              <strong>Improved Performance</strong> — Modern cloud
              infrastructure delivers faster processing
            </li>
            <li>
              <strong>Business Continuity</strong> — Built-in DR and HA
              configurations
            </li>
            <li>
              <strong>Zero Downtime Goal</strong> — Planned cutover windows with
              rollback capability
            </li>
          </ul>
        </div>
      </>
    ),

    MANAGED_SERVICES: (
      <>
        <div className="panel-section">
          <h2 className="panel-modal-title">Managed Services</h2>
          <p className="panel-modal-text">
            Our Managed Services offering provides ongoing, dedicated support
            for your ERP environment — covering application support, bug fixes,
            enhancements, performance optimization, and user training. Think of
            us as an extension of your IT team, available when you need us, at a
            predictable monthly cost.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Service Tiers:</h3>

          <h3 className="panel-modal-subtitle">🟢 Standard Support</h3>
          <ul className="panel-modal-list">
            <li>Business hours support (9 AM – 6 PM IST, Mon-Fri)</li>
            <li>Incident management and resolution</li>
            <li>Monthly system health checks</li>
            <li>Patch and update advisory</li>
            <li>Up to 40 hours/month of support effort</li>
          </ul>

          <h3 className="panel-modal-subtitle">🔵 Premium Support</h3>
          <ul className="panel-modal-list">
            <li>Extended hours support (8 AM – 10 PM IST, Mon-Sat)</li>
            <li>Priority incident response (SLA: 2 hours for critical)</li>
            <li>Bi-weekly system health checks and optimization</li>
            <li>Minor enhancements and configuration changes</li>
            <li>Up to 80 hours/month of support effort</li>
            <li>Dedicated account manager</li>
          </ul>

          <h3 className="panel-modal-subtitle">🟣 Enterprise Support</h3>
          <ul className="panel-modal-list">
            <li>24/7 support with on-call engineers</li>
            <li>Critical incident response (SLA: 30 minutes)</li>
            <li>Continuous monitoring and proactive alerts</li>
            <li>Major enhancements and new module implementations</li>
            <li>Unlimited support hours</li>
            <li>Dedicated team with functional and technical resources</li>
            <li>Quarterly business reviews and roadmap planning</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">What We Cover:</h3>
          <ul className="panel-modal-list">
            <li>
              <strong>Application Support</strong> — Bug fixes, issue
              investigation, root cause analysis
            </li>
            <li>
              <strong>Enhancements</strong> — New reports, workflows, approval
              rules, and UI customizations
            </li>
            <li>
              <strong>Integration Support</strong> — Monitoring, error
              resolution, and endpoint management
            </li>
            <li>
              <strong>Performance Optimization</strong> — Query tuning, index
              optimization, and load testing
            </li>
            <li>
              <strong>User Training</strong> — Ongoing training sessions for new
              hires and feature rollouts
            </li>
            <li>
              <strong>Upgrade Assistance</strong> — Quarterly patch analysis,
              testing, and deployment
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Key Benefits:</h3>
          <ul className="panel-modal-list">
            <li>
              <strong>Predictable Costs</strong> — Fixed monthly fee with no
              surprise charges
            </li>
            <li>
              <strong>Reduced Risk</strong> — Proactive monitoring catches
              issues before they impact users
            </li>
            <li>
              <strong>Faster Resolution</strong> — SLA-driven response and
              resolution times
            </li>
            <li>
              <strong>Knowledge Retention</strong> — Our team builds deep
              expertise in your specific environment
            </li>
            <li>
              <strong>Scalable</strong> — Start with Standard and scale to
              Enterprise as your needs grow
            </li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Supported Platforms:</h3>
          <p className="panel-modal-text">
            Oracle Fusion Cloud, Oracle EBS, Oracle NetSuite, JD Edwards, Odoo,
            Zoho, Microsoft Dynamics 365, Sage, Tally, and QuickBooks. We also
            support custom-built applications and integrations.
          </p>
        </div>
      </>
    ),
    AMC_SERVICES: (
      <>
        <div className="panel-section">
          <h2 className="panel-modal-title">
            AMC (Annual Maintenance Contract)
          </h2>
          <p className="panel-modal-text">
            Our Annual Maintenance Contract (AMC) service ensures your ERP
            systems, integrations, and custom developments remain stable,
            secure, and optimized throughout the year. With proactive
            monitoring, periodic maintenance, and rapid issue resolution, we
            help businesses avoid downtime and maintain peak system performance.
          </p>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">What AMC Covers:</h3>
          <ul className="panel-modal-list">
            <li>Regular system health checks and performance monitoring</li>
            <li>Bug fixes and issue resolution</li>
            <li>Minor enhancements and configuration updates</li>
            <li>Integration monitoring and troubleshooting</li>
            <li>Database optimization and performance tuning</li>
            <li>Periodic backups and disaster recovery checks</li>
            <li>Security patching and version updates</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Service Model:</h3>
          <ul className="panel-modal-list">
            <li>Dedicated technical support team</li>
            <li>Defined SLA for issue resolution</li>
            <li>Monthly system maintenance reports</li>
            <li>Quarterly performance and improvement review</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Key Benefits:</h3>
          <ul className="panel-modal-list">
            <li>Reduced system downtime and operational risk</li>
            <li>Faster issue resolution with dedicated support</li>
            <li>Continuous system optimization</li>
            <li>Predictable maintenance costs</li>
            <li>Improved security and compliance readiness</li>
          </ul>
        </div>

        <div className="panel-section">
          <h3 className="panel-modal-subtitle">Supported Platforms:</h3>
          <p className="panel-modal-text">
            Oracle Fusion, Oracle EBS, SAP, Odoo, NetSuite, Zoho, Microsoft
            Dynamics, and other ERP platforms including custom integrations.
          </p>
        </div>
      </>
    ),
  };

  return content[serviceKey] || null;
};

const ProductCardPanel = () => {
  const taxServices = [
    {
      label: "EWB Bill and Self Billing Reporting",
      serviceKey: "EWB",
    },
    {
      label: "Vendor Payment Blocking",
      serviceKey: "VENDOR_BLOCKING",
    },
    {
      label: "VAT Reconciliation – AP, AR and GL",
      serviceKey: "VAT_RECON",
    },
    {
      label: "Direct Tax - One-Way Sync",
      serviceKey: "DIRECT_TAX",
    },
    {
      label: "Bank Statement and Reconciliation",
      serviceKey: "BANK_RECON",
    },
  ];

  const nonTaxServices = [
    {
      label: "Oracle BIP, Oracle OIC, Oracle VBCS and Oracle APEX Development",
      serviceKey: "ORACLE_DEV",
    },
    {
      label: "Data Migration Projects",
      serviceKey: "DATA_MIGRATION",
    },
    {
      label: "ERP Lift & Shift",
      serviceKey: "ERP_LIFT_SHIFT",
    },
    {
      label: "Managed Services",
      serviceKey: "MANAGED_SERVICES",
    },
    {
      label: "AMC (Annual Maintenance Contract)",
      serviceKey: "AMC_SERVICES",
    },
  ];

  const renderServiceItems = (services) =>
    services.map((service, index) => (
      <React.Fragment key={service.serviceKey}>
        <Popup
          trigger={
            <div className="service-item service-item-clickable">
              <span className="service-item-text">{service.label}</span>
              <span className="service-item-arrow">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          }
          modal
          nested
        >
          {(close) => (
            <div className="panel-popup-wrapper">
              <button className="panel-popup-close" onClick={() => close()}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div className="panel-modal main-panel-scroll">
                <div className="panel-modal-content">
                  <ServicePopupContent serviceKey={service.serviceKey} />
                </div>
              </div>
            </div>
          )}
        </Popup>
        {index < services.length - 1 && <div className="divider"></div>}
      </React.Fragment>
    ));

  return (
    <section className="services-section">
      {/* <h1 className="services-main-title">Our Services</h1> */}

      <div className="services-wrapper">
        {/* Tax Arena */}
        <div className="service-panel">
          <div className="panel-label">Tax Arena</div>
          <div className="panel-content">{renderServiceItems(taxServices)}</div>
        </div>

        {/* Non-Tax Arena */}
        <div className="service-panel">
          <div className="panel-label">Non-Tax Arena</div>
          <div className="panel-content">
            {renderServiceItems(nonTaxServices)}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductCardPanel;
